import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { formatDistanceToNow } from 'date-fns';
import { enUS, vi } from 'date-fns/locale';
import i18n from 'i18next';
import { ChevronRight } from 'lucide-react';
import type { AuditLogEntry } from '@repo/shared-types';
import { useStudentAudit } from '../../../hooks/useStudents';
import { useCan } from '../../../hooks/useCan';
import { ActivityItem, ActivityList } from '../../../components/activity/ActivityItem';
import { auditActionIcon, auditActionLabelKey, formatVnStamp, hasAuditActionLabel, summarizeAudit } from '../../../lib/auditActions';

/** Same anatomy as a row on Cài đặt › Nhật ký thao tác, from the same lib:
 *  label + one-line summary, then the Vietnam-time stamp. No actor here — the
 *  student endpoint returns raw rows and the page is a per-student view. */
function AuditRow({ entry }: { entry: AuditLogEntry }) {
  const { t } = useTranslation('settings');
  const locale = i18n.language === 'vi' ? vi : enUS;
  const when = new Date(entry.createdAt);
  const summary = summarizeAudit(entry, t);
  const text = (
    <span className="block min-w-0">
      <span className="block">
        {hasAuditActionLabel(entry.action) ? (
          <span className="font-medium text-surface-800">{t(auditActionLabelKey(entry.action))}</span>
        ) : (
          <span className="rounded-[6px] bg-surface-100 px-[6px] py-[1px] font-mono text-[12px] text-surface-700">{entry.action}</span>
        )}
        {summary && <span className="text-surface-600"> · {summary}</span>}
      </span>
      <span className="mt-[2px] block text-xs tabular-nums text-surface-400">{formatVnStamp(when)}</span>
    </span>
  );
  return <ActivityItem icon={auditActionIcon(entry.action)} text={text} time={formatDistanceToNow(when, { locale, addSuffix: true })} />;
}

/**
 * "Hoạt động" on the student page — the same list anatomy the customer page
 * shows, fed by the audit log instead of client activities. It used to be
 * called "Lịch sử thao tác"; one name, one shape (13 §5).
 */
export function StudentActivitySection({ studentId }: { studentId: string }) {
  const { t } = useTranslation('students');
  const { data: entries } = useStudentAudit(studentId);
  const rows = entries ?? [];
  // THI-259: the whole-org log lives under Cài đặt; this is the doorway from
  // the place the question "ai đã sửa em này" is actually asked. Same gate
  // as the page itself, so a MEMBER never sees a link to a 403.
  const canSeeOrgLog = useCan('manage:settings');

  return (
    <section className="mt-8">
      <h2 className="mb-[9.6px] font-display text-[15px] font-semibold tracking-[-0.012em] text-surface-800">
        {t('detail.activity.title')}
      </h2>
      <ActivityList count={rows.length} collapseAfter={3} emptyLabel={t('detail.activity.empty')}>
        {rows.map((entry) => (
          <AuditRow key={entry.id} entry={entry} />
        ))}
      </ActivityList>
      {canSeeOrgLog && (
        <Link
          to="/settings/audit?group=students"
          className="mt-[9.6px] inline-flex min-h-[38.4px] items-center gap-[3.2px] text-[13px] font-medium text-brand-700 hover:text-brand-800"
        >
          {t('detail.activity.orgLog')}
          <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
        </Link>
      )}
    </section>
  );
}
